import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, Phone, Mail, PhoneCall, MapPin, X, ChevronLeft, ChevronRight, Tag } from 'lucide-react';
import { getInitials } from '@/lib/status';
import LeadSourceStatsPanel from '@/components/LeadSourceStatsPanel';

export default function LeadsPage() {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [showClosed, setShowClosed] = useState(false);
  const [sourceFilter, setSourceFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [eventFilter, setEventFilter] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(true);
  const [dd, setDd] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams({ page, search, closed: showClosed ? 'true' : 'false' });
      if (sourceFilter) qs.set('source', sourceFilter);
      if (typeFilter) qs.set('lead_type', typeFilter);
      if (eventFilter) qs.set('event_name', eventFilter);
      const [leadsRes, ddRes] = await Promise.all([
        api.get(`/leads?${qs.toString()}`),
        api.get('/dropdowns'),
      ]);
      setLeads(leadsRes.data.items);
      setTotal(leadsRes.data.total);
      setPages(leadsRes.data.pages || 1);
      setDd(ddRes.data || {});
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [api, page, search, showClosed, sourceFilter, typeFilter, eventFilter]);

  useEffect(() => { load(); }, [load]);

  const handleSave = async (e) => {
    e.preventDefault();
    await api.post('/leads', form);
    setShowForm(false);
    setForm({});
    load();
  };

  const clearFilters = () => { setSourceFilter(''); setTypeFilter(''); setEventFilter(''); setPage(1); };
  const hasFilters = !!(sourceFilter || typeFilter || eventFilter);

  return (
    <div data-testid="leads-page" className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight text-slate-900 font-[Manrope]">Leads</h1>
          <p className="text-sm text-slate-500 mt-1 font-medium">
            <span className="font-mono-num font-bold text-slate-700">{total.toLocaleString()}</span> {showClosed ? 'closed' : 'open'} {total === 1 ? 'lead' : 'leads'}
            {hasFilters && <span className="ml-2 text-amber-600">· filtered</span>}
          </p>
        </div>
        <button
          data-testid="add-lead-button"
          onClick={() => { setForm({ lead_date: new Date().toISOString().split('T')[0] }); setShowForm(true); }}
          className="flex items-center gap-2 px-4 py-2 bg-[#D35400] hover:bg-[#A04000] text-white rounded-md text-sm font-semibold shadow-sm hover:shadow-md hover:shadow-orange-500/20 transition-all"
        >
          <Plus size={16} strokeWidth={2.5} /> New Lead
        </button>
      </div>

      {/* Marketing rollups panel */}
      <LeadSourceStatsPanel
        activeSource={sourceFilter}
        onPickSource={(k) => { setSourceFilter(k); setPage(1); }}
      />

      <div className="flex gap-3 flex-wrap items-center">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            data-testid="leads-search"
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }}
            placeholder="Search by name, suburb, email, event…"
            className="w-full pl-10 pr-4 py-2.5 border border-[#E2E2DF] rounded-md bg-white text-sm"
          />
        </div>
        <select
          data-testid="leads-source-filter"
          value={sourceFilter}
          onChange={e => { setSourceFilter(e.target.value); setPage(1); }}
          className="px-3 py-2.5 border border-[#E2E2DF] rounded-md bg-white text-sm min-w-[150px]"
        >
          <option value="">All sources</option>
          {(dd.lead_sources || []).map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <select
          data-testid="leads-type-filter"
          value={typeFilter}
          onChange={e => { setTypeFilter(e.target.value); setPage(1); }}
          className="px-3 py-2.5 border border-[#E2E2DF] rounded-md bg-white text-sm min-w-[140px]"
        >
          <option value="">All types</option>
          {(dd.lead_types || []).map(t => <option key={t} value={t}>{t}</option>)}
        </select>
        <input
          data-testid="leads-event-filter"
          value={eventFilter}
          onChange={e => { setEventFilter(e.target.value); setPage(1); }}
          placeholder="Event / campaign…"
          className="px-3 py-2.5 border border-[#E2E2DF] rounded-md bg-white text-sm w-44"
        />
        {hasFilters && (
          <button
            data-testid="leads-clear-filters"
            onClick={clearFilters}
            className="px-3 py-2.5 border border-amber-200 bg-amber-50 text-amber-700 rounded-md text-xs font-semibold hover:bg-amber-100"
          >Clear filters ×</button>
        )}
        <button
          data-testid="leads-toggle-closed"
          onClick={() => { setShowClosed(!showClosed); setPage(1); }}
          className={`px-4 py-2.5 border rounded-md text-sm font-semibold transition-colors ${showClosed ? 'bg-slate-100 text-slate-700 border-slate-300' : 'border-[#E2E2DF] text-slate-600 hover:border-slate-300 hover:bg-slate-50'}`}
        >
          {showClosed ? 'Show Open' : 'Show Closed'}
        </button>
      </div>

      {loading ? (
        <div className="text-center py-12 text-slate-400">Loading...</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {leads.map(l => {
            const initials = getInitials(l.contact_first_name, l.contact_surname);
            const promoted = !!l.promoted_appraisal_id;
            return (
              <div
                key={l._id}
                data-testid={`lead-card-${l._id}`}
                className="bg-white border border-[#E2E2DF] rounded-md p-4 card-lift cursor-pointer relative overflow-hidden"
                onClick={() => navigate(`/leads/${l._id}`)}
              >
                <div className={`absolute inset-x-0 top-0 h-[3px] ${promoted ? 'bg-emerald-400' : l.lead_closed ? 'bg-slate-300' : 'bg-amber-400'}`} />
                <div className="flex items-start justify-between mb-3 gap-3">
                  <div className="flex items-start gap-3 min-w-0 flex-1">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ring-1 ${l.lead_closed ? 'bg-slate-50 ring-slate-200' : 'bg-gradient-to-br from-amber-50 to-amber-100/40 ring-amber-200/60'}`}>
                      <span className={`text-xs font-bold font-[Manrope] ${l.lead_closed ? 'text-slate-500' : 'text-amber-700'}`}>{initials}</span>
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-slate-800 truncate">{l.contact_first_name} {l.contact_surname}</p>
                      {(l.contact_suburb || l.contact_city) && (
                        <p className="text-xs text-slate-500 mt-0.5 flex items-center gap-1 truncate">
                          <MapPin size={11} className="text-slate-400 flex-shrink-0" />
                          {l.contact_suburb}{l.contact_city ? `, ${l.contact_city}` : ''}
                        </p>
                      )}
                    </div>
                  </div>
                  <span className={`text-[10px] font-semibold px-2 py-1 rounded-full ring-1 whitespace-nowrap ${promoted ? 'bg-emerald-50 text-emerald-700 ring-emerald-200' : l.lead_closed ? 'bg-slate-100 text-slate-500 ring-slate-200' : 'bg-amber-50 text-amber-700 ring-amber-100'}`}>
                    {promoted ? 'Promoted' : l.lead_closed ? 'Closed' : 'Open'}
                  </span>
                </div>
                <div className="space-y-1 pl-[52px]">
                  {(l.lead_source || l.lead_type) && (
                    <p className="text-xs text-slate-600 flex items-center gap-1.5">
                      <Tag size={11} className="text-slate-400 flex-shrink-0" />
                      {[l.lead_source, l.lead_type].filter(Boolean).join(' · ')}
                    </p>
                  )}
                  {l.event_name && (
                    <p className="text-[11px]">
                      <span className="px-1.5 py-0.5 bg-purple-50 text-purple-700 rounded-full font-semibold">{l.event_name}</span>
                    </p>
                  )}
                  {l.contact_phone && <p className="text-xs text-slate-600 flex items-center gap-1.5 font-mono-num"><Phone size={11} className="text-slate-400 flex-shrink-0" />{l.contact_phone}</p>}
                  {l.contact_email && <p className="text-xs text-slate-600 flex items-center gap-1.5 truncate"><Mail size={11} className="text-slate-400 flex-shrink-0" />{l.contact_email}</p>}
                </div>
                {l.lead_requirements && <p className="text-xs text-slate-500 mt-3 line-clamp-2 pl-[52px]">{l.lead_requirements}</p>}
              </div>
            );
          })}
          {leads.length === 0 && (
            <div className="col-span-full text-center py-16">
              <p className="text-sm text-slate-400 mb-1">No leads found</p>
              <p className="text-xs text-slate-300">Try adjusting your search or filters</p>
            </div>
          )}
        </div>
      )}

      {pages > 1 && (
        <div className="flex items-center justify-between pt-2">
          <span className="text-xs text-slate-400">Page {page} of {pages}</span>
          <div className="flex gap-1">
            <button data-testid="leads-prev-page" disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="p-2 rounded border border-slate-200 disabled:opacity-30"><ChevronLeft size={14} /></button>
            <button data-testid="leads-next-page" disabled={page >= pages} onClick={() => setPage(p => p + 1)} className="p-2 rounded border border-slate-200 disabled:opacity-30"><ChevronRight size={14} /></button>
          </div>
        </div>
      )}

      {showForm && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setShowForm(false)}>
          <div className="bg-white rounded-lg w-full max-w-2xl max-h-[88vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="p-5 border-b border-[#E2E2DF] flex items-center justify-between">
              <h2 className="text-xl font-bold text-slate-900 font-[Manrope]">New Lead</h2>
              <button onClick={() => setShowForm(false)} className="text-slate-400 hover:text-slate-700"><X size={18} /></button>
            </div>
            <form onSubmit={handleSave} className="p-5 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {[
                  { k: 'contact_first_name', l: 'First Name', r: true },
                  { k: 'contact_surname', l: 'Surname', r: true },
                  { k: 'contact_phone', l: 'Phone' },
                  { k: 'contact_mobile', l: 'Mobile' },
                  { k: 'contact_email', l: 'Email' },
                  { k: 'contact_street', l: 'Street' },
                  { k: 'contact_suburb', l: 'Suburb' },
                  { k: 'contact_city', l: 'City' },
                  { k: 'lead_person', l: 'Lead Captured By' },
                  { k: 'lead_date', l: 'Lead Date', t: 'date' },
                ].map(f => (
                  <div key={f.k}>
                    <label className="block text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500 mb-1">{f.l}</label>
                    <input
                      data-testid={`new-lead-${f.k.replace(/_/g, '-')}`}
                      type={f.t || 'text'}
                      required={f.r}
                      value={form[f.k] || ''}
                      onChange={e => setForm({ ...form, [f.k]: e.target.value })}
                      className="w-full px-3 py-2 border border-[#E2E2DF] rounded-md text-sm"
                    />
                  </div>
                ))}
                <div>
                  <label className="block text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500 mb-1">Lead Type</label>
                  <select data-testid="lead-type-select" value={form.lead_type || ''} onChange={e => setForm({ ...form, lead_type: e.target.value })} className="w-full px-3 py-2 border border-[#E2E2DF] rounded-md text-sm bg-white">
                    <option value="">Select...</option>
                    {(dd.lead_types || []).map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500 mb-1">Source Channel</label>
                  <select data-testid="lead-source-select" value={form.lead_source || ''} onChange={e => setForm({ ...form, lead_source: e.target.value })} className="w-full px-3 py-2 border border-[#E2E2DF] rounded-md text-sm bg-white">
                    <option value="">Select...</option>
                    {(dd.lead_sources || []).map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500 mb-1">Event / Campaign <span className="text-slate-400 font-normal normal-case">(optional)</span></label>
                  <input
                    data-testid="new-lead-event-name"
                    value={form.event_name || ''}
                    onChange={e => setForm({ ...form, event_name: e.target.value })}
                    placeholder='e.g. "Tonic Home Show 2026", "Letterbox Drop — Hamilton East"'
                    className="w-full px-3 py-2 border border-[#E2E2DF] rounded-md text-sm"
                  />
                </div>
              </div>
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500 mb-1">Requirements</label>
                <textarea
                  data-testid="new-lead-requirements"
                  value={form.lead_requirements || ''}
                  onChange={e => setForm({ ...form, lead_requirements: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border border-[#E2E2DF] rounded-md text-sm"
                />
              </div>
              <div className="flex items-center gap-2 text-xs text-slate-500">
                <PhoneCall size={11} className="text-slate-400" />
                After saving, open the lead to add follow-up notes and promote it to an Appraisal.
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 border border-[#E2E2DF] rounded-md text-sm hover:bg-slate-50">Cancel</button>
                <button data-testid="save-lead-button" type="submit" className="px-4 py-2 bg-[#D35400] hover:bg-[#A04000] text-white rounded-md text-sm font-semibold">Create Lead</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
